/** A full setting key, `<group>.<name>`. The same pattern is the `app_settings.key` CHECK. */
export const SETTING_KEY_RE = /^[a-z0-9_]+(\.[a-z0-9_]+)+$/;
const GROUP_ID_RE = /^[a-z0-9_]+(\.[a-z0-9_]+)*$/;
const NAME_RE = /^[a-z0-9_]+$/;
const ENV_NAME_RE = /^[A-Z][A-Z0-9_]*$/;
const SQL_IDENT_RE = /^[a-z_][a-z0-9_]*$/;
function fail(id, message) {
    throw new Error(`defineGroup('${id}'): ${message}`);
}
/** Declare a settings group. Throws on a declaration that could never save or resolve correctly. */
export function defineGroup(id, spec) {
    if (!GROUP_ID_RE.test(id))
        fail(id, `the group id must match ${GROUP_ID_RE}`);
    if (!spec.label?.trim())
        fail(id, 'label is required');
    if (!spec.editPermission?.startsWith('feature:'))
        fail(id, 'editPermission must be a feature:<key> permission');
    const names = Object.keys(spec.settings);
    if (names.length === 0)
        fail(id, 'a group needs at least one setting');
    const refs = (spec.refs ?? {});
    for (const name of Object.keys(refs)) {
        if (!names.includes(name))
            fail(id, `refs.${name} names no setting of the group`);
        const ref = refs[name];
        if (!SQL_IDENT_RE.test(ref.table) || !SQL_IDENT_RE.test(ref.column ?? 'id'))
            fail(id, `refs.${name}: table and column must be plain identifiers`);
        if (!ref.code?.trim())
            fail(id, `refs.${name}: code is required`);
    }
    const settings = {};
    for (const name of names) {
        const s = spec.settings[name];
        const key = `${id}.${name}`;
        if (!NAME_RE.test(name) || !SETTING_KEY_RE.test(key))
            fail(id, `setting "${name}": the key ${key} must match ${SETTING_KEY_RE}`);
        if (!s.label?.trim())
            fail(id, `${name}: label is required`);
        if (!s.type.safeParse(s.default).success)
            fail(id, `${name}: the default does not pass its own type`);
        if (s.env !== undefined && !ENV_NAME_RE.test(s.env))
            fail(id, `${name}: env must be an UPPER_SNAKE variable name`);
        if (s.envParse !== undefined && s.env === undefined)
            fail(id, `${name}: envParse needs env`);
        settings[name] = Object.freeze({ ...s, key, name, group: id, ...(refs[name] ? { ref: refs[name] } : {}) });
    }
    if (spec.validate) {
        const defaults = Object.fromEntries(names.map((n) => [n, settings[n].default]));
        const err = spec.validate(defaults);
        if (err)
            fail(id, `the defaults fail the group's own validate (${err.code})`);
    }
    return Object.freeze({
        id,
        label: spec.label,
        editPermission: spec.editPermission,
        readPermission: Object.freeze([...(spec.readPermission ?? [])]),
        requireReason: spec.requireReason ?? false,
        aigOnly: spec.aigOnly ?? false,
        editedOn: spec.editedOn ?? null,
        settings: Object.freeze(settings),
        keys: Object.freeze(names.map((n) => settings[n].key)),
        ...(spec.validate ? { validate: spec.validate } : {}),
    });
}
//# sourceMappingURL=define.js.map